import React, { useState, useEffect } from 'react';
import { 
  Search, User, Mail, Phone, 
  Loader2, Trash2, CheckCircle2, FileText
} from 'lucide-react';
import { db } from '../firebase';
import { collection, getDocs, doc, deleteDoc, updateDoc } from 'firebase/firestore';

const ApplicationsPage = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('All');
  const [statusMessage, setStatusMessage] = useState('');

  const fetchApps = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'applications'));
      const list = [];
      snap.forEach(d => {
        list.push({ id: d.id, ...d.data() });
      });
      // Sort newest first
      list.sort((a, b) => {
        const timeA = a.createdAt?.seconds || 0;
        const timeB = b.createdAt?.seconds || 0;
        return timeB - timeA;
      });
      setApps(list);
    } catch (e) {
      console.error('Error fetching applications from Firebase:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApps();
  }, []);

  const handleStatusChange = async (id, newStatus) => {
    try {
      await updateDoc(doc(db, 'applications', id), { status: newStatus });
      setApps(prev => prev.map(a => a.id === id ? { ...a, status: newStatus } : a));
      setStatusMessage('Status updated successfully');
      setTimeout(() => setStatusMessage(''), 2500);
    } catch (err) {
      alert('Error updating status: ' + err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this application record?')) return;
    try {
      await deleteDoc(doc(db, 'applications', id));
      setApps(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      alert('Error deleting application: ' + err.message);
    }
  };

  const handleViewResume = (app) => {
    if (!app.resume) {
      alert('No resume file attached for this application.');
      return;
    }
    // If it's a data url or base64
    if (app.resume.startsWith('data:')) {
      const win = window.open();
      win.document.write(`<iframe src="${app.resume}" frameborder="0" style="border:0; top:0px; left:0px; bottom:0px; right:0px; width:100%; height:100%;" allowfullscreen></iframe>`);
    } else {
      window.open(app.resume, '_blank');
    }
  };

  const filtered = apps.filter(a => {
    const matchesQuery = 
      (a.name || '').toLowerCase().includes(query.toLowerCase()) || 
      (a.jobTitle || '').toLowerCase().includes(query.toLowerCase()) ||
      (a.email || '').toLowerCase().includes(query.toLowerCase());
    const matchesRole = roleFilter === 'All' || a.roleType === roleFilter;
    return matchesQuery && matchesRole;
  });

  return (
    <div className="space-y-6">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-3xl font-black text-slate-900">Applicant Tracking</h2>
          <p className="text-xs text-slate-500 mt-1">Live candidate job applications stored in Firebase database.</p>
        </div>

        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          {/* Role Filter */}
          <div className="flex bg-white rounded-xl border border-slate-200 p-1">
            {['All', 'IT', 'Non-IT'].map(type => (
              <button
                key={type}
                onClick={() => setRoleFilter(type)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                  roleFilter === type ? 'bg-slate-900 text-white' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {type}
              </button>
            ))}
          </div>

          {/* Search bar */}
          <div className="relative flex-grow md:flex-grow-0">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 h-4 w-4" />
            <input 
              placeholder="Search candidate, role..." 
              value={query}
              onChange={e => setQuery(e.target.value)}
              className="pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-xl outline-none text-xs w-full md:w-64 focus:border-blue-500"
            />
          </div>

          <button
            onClick={fetchApps}
            className="px-3 py-2 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl text-xs font-bold text-slate-700"
          >
            Refresh
          </button>
        </div>
      </div>

      {statusMessage && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl text-xs font-bold flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-emerald-600" /> {statusMessage}
        </div>
      )}

      {/* Applications Table Card */}
      <div className="bg-white rounded-3xl border border-slate-100 shadow-xl overflow-hidden">
        {loading ? (
          <div className="p-20 flex flex-col items-center justify-center gap-3 text-slate-400">
            <Loader2 className="animate-spin text-amber-500 h-8 w-8" />
            <span className="text-xs font-medium">Fetching applicant records from Firebase...</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center text-slate-400">
            <User className="h-12 w-12 mx-auto mb-2 opacity-30" />
            <p className="font-bold text-slate-600">No applications found</p>
            <p className="text-xs mt-1">When candidates apply for IT or Non-IT roles, their submissions will appear here in real time.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead className="bg-slate-50/80 border-b border-slate-100 text-slate-500 text-[11px] font-black uppercase tracking-wider">
                <tr>
                  <th className="p-4 pl-6">Candidate</th>
                  <th className="p-4">Applied Role</th>
                  <th className="p-4">Date</th>
                  <th className="p-4">Status</th>
                  <th className="p-4">Resume</th>
                  <th className="p-4 pr-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {filtered.map((app) => (
                  <tr key={app.id} className="hover:bg-slate-50/60 transition">
                    <td className="p-4 pl-6">
                      <p className="font-bold text-slate-900">{app.name}</p>
                      <div className="flex flex-col gap-0.5 mt-1 text-[11px] text-slate-500">
                        <a href={`mailto:${app.email}`} className="flex items-center gap-1.5 hover:text-blue-600">
                          <Mail className="h-3 w-3 text-slate-400" /> {app.email}
                        </a>
                        {app.number && (
                          <a href={`tel:${app.number}`} className="flex items-center gap-1.5 hover:text-blue-600">
                            <Phone className="h-3 w-3 text-slate-400" /> {app.number}
                          </a>
                        )}
                      </div>
                    </td>

                    <td className="p-4">
                      <span className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-black uppercase ${
                        app.roleType === 'IT' ? 'bg-purple-100 text-purple-800' : 'bg-orange-100 text-orange-800'
                      }`}>
                        {app.jobTitle}
                      </span>
                      {app.jobCategory && (
                        <p className="text-[10px] text-slate-400 mt-1 font-semibold">{app.jobCategory}</p>
                      )}
                    </td>

                    <td className="p-4 text-xs text-slate-500">
                      {app.createdAtString || (app.createdAt?.seconds ? new Date(app.createdAt.seconds * 1000).toLocaleDateString() : 'Recent')}
                    </td>

                    <td className="p-4">
                      <select
                        value={app.status || 'New'}
                        onChange={(e) => handleStatusChange(app.id, e.target.value)}
                        className={`text-xs font-bold rounded-lg px-2.5 py-1 border outline-none cursor-pointer ${
                          app.status === 'Shortlisted' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                          app.status === 'Contacted' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                          app.status === 'Rejected' ? 'bg-rose-50 text-rose-700 border-rose-200' :
                          'bg-amber-50 text-amber-700 border-amber-200'
                        }`}
                      >
                        <option value="New">New</option>
                        <option value="Reviewed">Reviewed</option>
                        <option value="Contacted">Contacted</option>
                        <option value="Shortlisted">Shortlisted</option>
                        <option value="Rejected">Rejected</option>
                      </select>
                    </td>

                    <td className="p-4">
                      {app.resume ? (
                        <button
                          onClick={() => handleViewResume(app)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 text-xs font-bold transition"
                        >
                          <FileText className="h-3.5 w-3.5" /> View Resume
                        </button>
                      ) : (
                        <span className="text-xs text-slate-400 italic">None</span>
                      )}
                    </td>

                    <td className="p-4 pr-6 text-right">
                      <button
                        onClick={() => handleDelete(app.id)}
                        title="Delete Application"
                        className="p-2 text-slate-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApplicationsPage;
